import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { CallResult, DialerQueueItem, FlowOption } from '../api/types'
import { Icon } from '../components/Icon'
import { Empty, ErrorState, Loading } from '../components/States'
import { useToast } from '../components/Toast'
import { useAsync } from '../lib/useAsync'
import { hangUp, placeCall, type SoftphoneState } from '../lib/softphone'

/** Why this person can be called, in two words. The queue shows hundreds it cannot
 *  dial, so the chip carries the reason or the screen just looks broken. */
const BASIS = {
  consent: { label: 'Consented', tone: 'ok' },
  ebr: { label: 'Knows us', tone: 'ok' },
  cold: { label: 'Cold', tone: 'warn' },
} as const

function fmtPhone(raw: string | null) {
  const d = (raw || '').replace(/\D/g, '').slice(-10)
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : raw || '—'
}

/** A datetime-local value → ISO, for the two outcomes that need a time. */
function toIso(local: string) {
  return local ? new Date(local).toISOString() : ''
}

/** The calling screen.
 *
 *  A call has stages, and what an agent can say depends on how far it got: about three
 *  dials in four end before anyone speaks, and the only outcome that earns anything is
 *  a transfer. So the common case is one click, the valuable one is the only primary
 *  button on its stage, and the server owns the flow (voice.CALL_FLOW) instead of it
 *  being reinvented here. */
export function Dialer() {
  const [params] = useSearchParams()
  const [ownerLo, setOwnerLo] = useState<number | null>(null)
  const status = useAsync(() => api.callingStatus(), [])
  const queue = useAsync(() => api.callingQueue(ownerLo ?? undefined), [ownerLo])
  const [selected, setSelected] = useState<DialerQueueItem | null>(null)
  // Arrived from someone's page with "Phone" — put them in the chair rather than
  // making the officer find them again in a queue of hundreds.
  const asked = Number(params.get('contact')) || null
  useEffect(() => {
    if (!asked || selected) return
    const found = (queue.data?.items ?? []).find((i: DialerQueueItem) => i.contact_id === asked)
    if (found) setSelected(found)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asked, queue.data])
  const [call, setCall] = useState<CallResult | null>(null)
  const [stage, setStage] = useState('dialed')
  const [notes, setNotes] = useState('')
  const [when, setWhen] = useState('')
  const [pendingTime, setPendingTime] = useState<FlowOption | null>(null)
  const [transferTo, setTransferTo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [line, setLine] = useState<SoftphoneState>('offline')
  const [lineDetail, setLineDetail] = useState('')
  const toast = useToast()

  const st = status.data
  const flow = st?.flow ?? []
  const current = flow.find((f) => f.stage === stage)

  function reset() {
    hangUp()
    setCall(null)
    setStage('dialed')
    setNotes('')
    setWhen('')
    setPendingTime(null)
    setTransferTo(null)
  }

  function onLine(s: SoftphoneState, detail?: string) {
    setLine(s)
    setLineDetail(detail || '')
    if (s === 'error' && detail) toast(detail, { tone: 'danger' })
  }

  async function place(item: DialerQueueItem) {
    setBusy(true)
    try {
      const res = await api.startCall(item.contact_id)
      reset()
      setCall(res)
      if (res.status === 'simulated') {
        toast('Logged — simulated, nothing dialed', { tone: 'neutral' })
      } else {
        // Connecting the browser is what triggers the server to dial the borrower, so
        // the officer is in the room before their phone rings.
        await placeCall(res.call_id, onLine)
        toast('Connecting…', { tone: 'positive' })
      }
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  async function finish(disposition: string, callbackAt?: string) {
    if (!call) return
    setBusy(true)
    try {
      await api.disposeCall(call.call_id, {
        disposition,
        notes: notes.trim() || undefined,
        callback_at: callbackAt || undefined,
      })
      toast(st?.dispositions[disposition] ?? 'Call logged', { tone: 'positive' })
      reset()
      setSelected(null)
      queue.reload()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  async function doTransfer() {
    if (!call) return
    setBusy(true)
    try {
      const res = await api.transferCall(call.call_id)
      setTransferTo(res.lo)
      toast(res.simulated ? `Transfer to ${res.lo} (simulated)` : `Ringing ${res.lo}…`,
        { tone: 'positive' })
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
      setStage('transfer_failed')
    } finally {
      setBusy(false)
    }
  }

  async function resume() {
    if (!call) return
    setBusy(true)
    try {
      await api.cancelTransfer(call.call_id)
      setTransferTo(null)
      setStage('contacted')
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  async function record() {
    if (!call) return
    const said = window.prompt(
      'Recording needs the borrower’s agreement on the line first.\n\n' +
      'Maryland, California and Florida require everyone on the call to consent, so what ' +
      'they said is stored as the basis for the recording.\n\nWhat did they say?')
    if (!said) return
    setBusy(true)
    try {
      await api.recordCall(call.call_id, said)
      toast('Recording started', { tone: 'positive' })
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  function choose(opt: FlowOption) {
    if (opt.needs === 'time' && !pendingTime) { setPendingTime(opt); return }
    if (opt.advance) { setStage(opt.advance); return }
    if (opt.action === 'transfer') { setStage('transfer_watch'); void doTransfer(); return }
    if (opt.action === 'resume') { void resume(); return }
    if (opt.key) void finish(opt.key)
  }

  if (status.loading || queue.loading) return <Loading label="Opening the dialer…" />
  if (status.error) return <ErrorState error={status.error} onRetry={status.reload} />
  if (queue.error) return <ErrorState error={queue.error} onRetry={queue.reload} />

  const items = queue.data?.items ?? []
  const officers = queue.data?.officers ?? []

  return (
    <div className="page" style={{ maxWidth: 1180 }}>
      <div className="page-head">
        <div>
          <h1 className="h1">Dialer</h1>
          <p className="subline">
            {queue.data?.callable ?? 0} of {items.length} ready to call · calling hours {st?.calling_hours}
          </p>
        </div>
        <label className="dialer-filter">
          <span className="subline">Whose list</span>
          <select className="field" value={ownerLo ?? ''} aria-label="Filter by loan officer"
            onChange={(e) => { setOwnerLo(e.target.value ? Number(e.target.value) : null); setSelected(null) }}>
            <option value="">Everyone</option>
            {officers.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
      </div>

      {st && (st.mode !== 'live' || st.caller_id_problem || !st.cold_calling_open) ? (
        <section className="card" style={{ marginBottom: 16 }}>
          <div className="card-body" style={{ display: 'grid', gap: 6 }}>
            {st.mode !== 'live' ? (
              <p className="subline"><strong>Simulated.</strong> Every step is logged exactly as it
                would be — transfers included — but nothing is dialed.</p>
            ) : null}
            {st.caller_id_problem ? <p className="subline"><strong>No dialing number.</strong> {st.caller_id_problem}</p> : null}
            {!st.cold_calling_open ? <p className="subline"><strong>Cold calling is closed.</strong> {st.cold_calling_blocked_because}</p> : null}
          </div>
        </section>
      ) : null}

      <div className="dialer-split" data-view={selected ? 'stage' : 'queue'}>
        <section className="card dialer-queue">
          <div className="card-body" style={{ padding: 0 }}>
            {items.length === 0 ? (
              <Empty title="Nobody to call">Contacts show up here once they have a phone.</Empty>
            ) : (
              <ul className="dialer-list">
                {items.map((item) => {
                  const basis = BASIS[item.basis] ?? BASIS.cold
                  return (
                    <li key={item.contact_id}>
                      <button
                        className={`dialer-row${selected?.contact_id === item.contact_id ? ' active' : ''}${item.dialable ? '' : ' muted'}`}
                        onClick={() => { setSelected(item); reset() }}
                        aria-current={selected?.contact_id === item.contact_id}>
                        <span className="dialer-row-main">
                          <strong>{item.name || `Contact ${item.contact_id}`}</strong>
                          <small>{fmtPhone(item.phone)} · {item.assigned_lo ?? 'unassigned'}</small>
                        </span>
                        <span className={`pill ${basis.tone}`}>{basis.label}</span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </section>

        <section className="card dialer-stage">
          <div className="card-body">
            {!selected ? (
              <Empty title="Pick someone to call">
                Ready-to-call people are listed first. The rest show why they are not.
              </Empty>
            ) : (
              <>
                {/* Only rendered on a narrow screen (CSS). On a phone the queue is a
                    separate view, so there has to be a way back to it. */}
                <button className="dialer-back" onClick={() => { setSelected(null); reset() }}>
                  <Icon name="arrow_back" size={16} /> All {items.length}
                </button>
                <h2 className="h2" style={{ marginTop: 0 }}>{selected.name || `Contact ${selected.contact_id}`}</h2>
                <p className="subline">
                  {[selected.city, selected.state].filter(Boolean).join(', ')} · {fmtPhone(selected.phone)}
                  {selected.campaign ? ` · ${selected.campaign}` : ''}
                </p>
                <p className="subline"><strong>{BASIS[selected.basis]?.label}:</strong> {selected.basis_detail}</p>
                {/* Who the hot transfer reaches — known before the call, not discovered
                    mid-pitch. */}
                <p className="subline">
                  <strong>Transfers to:</strong>{' '}
                  {selected.assigned_lo
                    ? `${selected.assigned_lo}${selected.transfer_ready ? '' : ' — no phone on file, a transfer would fail'}`
                    : 'nobody assigned — assign an officer before calling'}
                </p>

                {!selected.dialable ? (
                  <div className="notice" role="status" style={{ marginTop: 12 }}>{selected.reason}</div>
                ) : null}

                {!call ? (
                  <button className="btn primary lg" style={{ marginTop: 16 }}
                    disabled={!selected.dialable || busy} onClick={() => place(selected)}>
                    <Icon name="call" size={18} />
                    {busy ? 'Starting…' : selected.dialable ? `Call ${fmtPhone(selected.phone)}` : 'Cannot call'}
                  </button>
                ) : (
                  <div className="dialer-live">
                    {call.status !== 'simulated' ? (
                      <p className="subline dialer-line" data-state={line}>
                        {line === 'connecting' ? 'Connecting your microphone…'
                          : line === 'on-call' ? `On the call — they see ${fmtPhone(st?.caller_id ?? null)}`
                          : line === 'error' ? `Line problem: ${lineDetail}`
                          : line === 'ready' ? 'Call ended.'
                          : 'Starting the line…'}
                      </p>
                    ) : null}
                    <textarea className="field" rows={3} value={notes} aria-label="Call notes"
                      placeholder="Notes — what they said, what to pass to the officer"
                      onChange={(e) => setNotes(e.target.value)} />

                    {stage === 'transfer_watch' ? (
                      <div className="dialer-step">
                        <p className="subline">
                          {transferTo ? `Announcing to ${transferTo}. Hand over once they're ready.` : 'Transferring…'}
                        </p>
                        <div className="dialer-options">
                          <button className="btn primary" disabled={busy || !transferTo}
                            onClick={() => finish('transferred')}>Handed over</button>
                          <button className="btn" disabled={busy}
                            onClick={() => setStage('transfer_failed')}>They didn’t pick up</button>
                        </div>
                      </div>
                    ) : pendingTime ? (
                      <div className="dialer-step">
                        <p className="subline">{pendingTime.label} — when?</p>
                        <div className="dialer-options">
                          <input className="field" type="datetime-local" value={when}
                            aria-label="When" onChange={(e) => setWhen(e.target.value)} />
                          <button className="btn primary" disabled={busy || !when}
                            onClick={() => finish(pendingTime.key!, toIso(when))}>Save</button>
                          <button className="btn" disabled={busy} onClick={() => setPendingTime(null)}>Back</button>
                        </div>
                      </div>
                    ) : current ? (
                      <div className="dialer-step">
                        <p className="subline">{current.question}</p>
                        <div className="dialer-options">
                          {current.options.map((opt) => (
                            <button key={opt.label}
                              className={`btn${opt.primary ? ' primary' : ''}${opt.tone === 'danger' ? ' danger' : ''}`}
                              disabled={busy || (opt.action === 'transfer' && !selected.transfer_ready)}
                              onClick={() => choose(opt)}>
                              {opt.action === 'transfer' && selected.assigned_lo
                                ? `Interested — transfer to ${selected.assigned_lo}`
                                : opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    <div className="dialer-foot">
                      <button className="btn subtle" disabled={busy} onClick={record}>Record (ask first)</button>
                      <button className="btn subtle" disabled={busy} onClick={reset}>Cancel</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}
